import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient } from "../lib/api";

const ClinicAuthContext = createContext(null);
const TOKEN_KEY = "alta_clinic_token";

export function ClinicAuthProvider({ children }) {
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((token) => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete apiClient.defaults.headers.common["Authorization"];
    }
  }, []);

  // Initial: try /me with existing token
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    apiClient.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    apiClient.get("/clinics/auth/me")
      .then((r) => setClinic(r.data))
      .catch(() => { applyToken(null); })
      .finally(() => setLoading(false));
  }, [applyToken]);

  const login = async (email, password) => {
    const { data } = await apiClient.post("/clinics/auth/login", { email, password });
    applyToken(data.token);
    setClinic(data.clinic);
    return data.clinic;
  };

  const register = async (payload) => {
    const { data } = await apiClient.post("/clinics/auth/register", payload);
    applyToken(data.token);
    setClinic(data.clinic);
    return data.clinic;
  };

  const logout = () => {
    applyToken(null);
    setClinic(null);
  };

  const refresh = async () => {
    const { data } = await apiClient.get("/clinics/auth/me");
    setClinic(data);
    return data;
  };

  const updateProfile = async (patch) => {
    const { data } = await apiClient.patch("/clinics/me", patch);
    setClinic(data);
    return data;
  };

  return (
    <ClinicAuthContext.Provider value={{ clinic, loading, login, register, logout, refresh, updateProfile }}>
      {children}
    </ClinicAuthContext.Provider>
  );
}

export const useClinicAuth = () => useContext(ClinicAuthContext);
